<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { api } from "../lib/api";
  import type { PodInfo } from "@antares/shared";
  import Terminal from "../components/Terminal.svelte";
  import LogViewer from "../components/LogViewer.svelte";
  import FileExplorer from "../components/FileExplorer.svelte";
  import MetricsPanel from "../components/MetricsPanel.svelte";
  import PodControls from "../components/PodControls.svelte";

  interface Props {
    ns: string;
    name: string;
  }

  let { ns, name }: Props = $props();

  let pod = $state<PodInfo | null>(null);
  let podGone = $state(false);
  let activeTab = $state<"console" | "logs" | "files" | "metrics">("logs");
  let tabInitialized = false;
  let errorMsg = $state("");
  let pollInterval: ReturnType<typeof setInterval>;
  let redirectTimeout: ReturnType<typeof setTimeout>;

  async function refreshPod() {
    try {
      pod = await api.getPod(ns, name);
      podGone = false;
      if (!tabInitialized) {
        activeTab = pod.mode === "managed" ? "console" : "logs";
        tabInitialized = true;
      }
    } catch (err: any) {
      if (err.message === "Unauthorized") return;
      // Pod likely deleted (404) or unavailable
      if (!podGone) {
        podGone = true;
        errorMsg = "";
        redirectTimeout = setTimeout(() => {
          window.location.hash = "/";
        }, 4000);
      }
    }
  }

  function handleChildDisconnect() {
    if (!podGone) refreshPod();
  }

  onMount(() => {
    refreshPod();
    pollInterval = setInterval(refreshPod, 10_000);
  });

  onDestroy(() => {
    clearInterval(pollInterval);
    clearTimeout(redirectTimeout);
  });

  const container = $derived(pod?.containers[0]?.name);
  const isManaged = $derived(pod?.mode === "managed");
</script>

<div class="pod-detail">
  <div class="pod-header">
    <div>
      <a href="#/" class="back">&larr; Workloads</a>
      <h2>{pod?.displayName ?? name}</h2>
      <span class="pod-id">{ns}/{name}</span>
      {#if pod}
        <span class="status" class:running={pod.status === "Running"} class:failed={pod.status === "Failed"}>
          {pod.status}
        </span>
      {/if}
    </div>
    {#if pod}
      <PodControls {ns} {name} status={pod.status} onAction={refreshPod} />
    {/if}
  </div>

  {#if podGone}
    <div class="gone-banner">
      This pod no longer exists. Returning to dashboard...
      <a href="#/" class="gone-link">Go now</a>
    </div>
  {:else if errorMsg}
    <div class="error-banner">{errorMsg}</div>
  {/if}

  <div class="tabs">
    {#if isManaged}
      <button class:active={activeTab === "console"} onclick={() => activeTab = "console"}>Console</button>
    {/if}
    <button class:active={activeTab === "logs"} onclick={() => activeTab = "logs"}>Logs</button>
    {#if isManaged}
      <button class:active={activeTab === "files"} onclick={() => activeTab = "files"}>Files</button>
    {/if}
    <button class:active={activeTab === "metrics"} onclick={() => activeTab = "metrics"}>Metrics</button>
  </div>

  {#if container && isManaged}
    <div class="tab-panel" class:hidden={activeTab !== "console"}>
      <Terminal {ns} {name} {container} onDisconnect={handleChildDisconnect} />
    </div>
  {/if}
  {#if container}
    <div class="tab-panel" class:hidden={activeTab !== "logs"}>
      <LogViewer {ns} {name} {container} onDisconnect={handleChildDisconnect} />
    </div>
  {/if}
  {#if container && isManaged}
    <div class="tab-panel" class:hidden={activeTab !== "files"}>
      <FileExplorer {ns} {name} {container} fsRoot={pod?.fsRoot ?? "/"} />
    </div>
  {/if}
  <div class="tab-panel" class:hidden={activeTab !== "metrics"}>
    <MetricsPanel {ns} {name} />
  </div>
</div>

<style>
  .pod-detail {
    max-width: 1200px;
  }

  .pod-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 1rem;
  }

  .back {
    color: var(--text-muted);
    text-decoration: none;
    font-size: 0.8rem;
  }

  .back:hover {
    color: var(--accent);
  }

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 0.25rem;
    font-family: var(--font-mono);
  }

  .pod-id {
    display: block;
    font-size: 0.7rem;
    font-family: var(--font-mono);
    color: var(--text-muted);
    opacity: 0.7;
  }

  .status {
    display: inline-block;
    font-size: 0.75rem;
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
    margin-top: 0.25rem;
    background: var(--bg-elevated);
    color: var(--text-muted);
  }

  .status.running { color: var(--success); background: rgba(74, 222, 128, 0.1); }
  .status.failed { color: var(--danger); background: rgba(248, 113, 113, 0.1); }

  .gone-banner {
    background: rgba(251, 191, 36, 0.1);
    border: 1px solid var(--warning);
    color: var(--warning);
    padding: 0.75rem 1rem;
    border-radius: 4px;
    margin-bottom: 1rem;
    font-size: 0.85rem;
  }

  .gone-link {
    display: inline-block;
    margin-left: 0.5rem;
    color: var(--accent);
    text-decoration: underline;
  }

  .error-banner {
    background: rgba(248, 113, 113, 0.1);
    border: 1px solid var(--danger);
    color: var(--danger);
    padding: 0.75rem 1rem;
    border-radius: 4px;
    margin-bottom: 1rem;
    font-size: 0.85rem;
  }

  .tabs {
    display: flex;
    gap: 0;
    border-bottom: 1px solid var(--border);
    margin-bottom: 1rem;
  }

  .tabs button {
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    color: var(--text-muted);
    padding: 0.5rem 1rem;
    cursor: pointer;
    font-size: 0.85rem;
  }

  .tabs button:hover {
    color: var(--text);
  }

  .tabs button.active {
    color: var(--accent);
    border-bottom-color: var(--accent);
  }

  .tab-panel {
    min-height: 400px;
  }

  .tab-panel.hidden {
    display: none;
  }
</style>
